"use client";

import { forwardRef } from "react";

import { classNames } from "@/modules/telegram-ui-custom/helpers/classNames";
import { AppRootContext } from "@/modules/telegram-ui-custom/components/Service/AppRoot/AppRootContext";
import { useAppearance } from "@/modules/telegram-ui-custom/components/Service/AppRoot/hooks/useAppearance";
import { useObjectMemo } from "@/modules/telegram-ui-custom/hooks/useObjectMemo";
import { multipleRef } from "@/modules/telegram-ui-custom/helpers/react/refs";

import type { AppRootContextInterface } from "@/modules/telegram-ui-custom/components/Service/AppRoot/AppRootContext";
import type { HTMLAttributes } from "react";

import styles from "./AppRoot.module.css";
import { usePlatform } from "./hooks/usePlatform";
import { usePortalContainer } from "./hooks/usePortalContainer";

export interface AppRootProps extends HTMLAttributes<HTMLDivElement> {
  /** Application platform, determined automatically if nothing passed */
  platform?: AppRootContextInterface["platform"];
  /** Application appearance, determined automatically if nothing passed */
  appearance?: AppRootContextInterface["appearance"];
  /** Rewriting portal container for rendering, AppRoot container as default */
  portalContainer?: AppRootContextInterface["portalContainer"];
}

export const AppRoot = forwardRef<HTMLDivElement, AppRootProps>(
  (
    {
      platform: platformProp,
      appearance: appearanceProp,
      portalContainer: portalContainerProp,
      children,
      className,
      ...restProps
    },
    ref,
  ) => {
    const appearance = useAppearance(appearanceProp);
    const portalContainer = usePortalContainer(portalContainerProp);
    const platform = usePlatform(platformProp);

    const contextValue = useObjectMemo({
      platform,
      appearance,
      portalContainer,
      isRendered: true,
    });

    return (
      <div
        ref={multipleRef(ref, portalContainer)}
        className={classNames(
          styles.wrapper,
          platform === "ios" && styles["wrapper--ios"],
          appearance === "dark" && styles["wrapper--dark"],
          className,
        )}
        {...restProps}
      >
        <AppRootContext.Provider value={contextValue}>
          {children}
        </AppRootContext.Provider>
      </div>
    );
  },
);

AppRoot.displayName = "AppRoot";
